// Shared command-history list — used by the Leitstand ("Was ist zuletzt passiert?")
// and the Geraet detail page (per-point Fernbefehle history). Status glyph mirrors
// the mockup's ⏳ wartet → → unterwegs → ✓ erledigt progression.
import { useTranslation } from 'react-i18next'
import { EmptyState, Skeleton } from '../../../shared/ui'
import type { HaccpCommandRow } from '../haccpApi'

function statusOf(row: HaccpCommandRow): { icon: string; tone: string; label: string } {
  if (row.replied_at) {
    const failed = !!row.result_code && /fail|error|nok/i.test(row.result_code)
    return failed
      ? { icon: '✗', tone: 'text-rose-600 dark:text-rose-400', label: 'failed' }
      : { icon: '✓', tone: 'text-emerald-600 dark:text-emerald-400', label: 'acked' }
  }
  if (row.sent_at) return { icon: '→', tone: 'text-sky-600 dark:text-sky-400', label: 'sent' }
  return { icon: '⏳', tone: 'text-on-surface-variant', label: 'queued' }
}

function formatTs(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

interface Props {
  rows: HaccpCommandRow[] | undefined
  isLoading: boolean
  /** Show the point/school context is not available here — caller decides scope via the query. */
  emptyIcon?: string
}

export function CommandHistoryList({ rows, isLoading, emptyIcon = 'history' }: Props) {
  const { t } = useTranslation('haccp')

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map(i => <Skeleton key={i} className="h-9 w-full rounded-lg" />)}
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return <EmptyState icon={emptyIcon} title={t('leitstand.recent.empty')} />
  }

  return (
    <div className="divide-y divide-outline-variant/20">
      {rows.map((row) => {
        const st = statusOf(row)
        return (
          <div key={row.cmd_id} className="flex items-center gap-3 py-2 text-sm">
            <span className={`w-5 text-center flex-shrink-0 ${st.tone}`} aria-hidden="true">{st.icon}</span>
            <span className="text-xs text-on-surface-variant tabular-nums w-[92px] flex-shrink-0">{formatTs(row.queued_at)}</span>
            <span className="flex-1 min-w-0 truncate text-on-surface">{row.command_text ?? row.command_key}</span>
            {row.queued_by && <span className="text-xs text-on-surface-variant flex-shrink-0">{row.queued_by}</span>}
          </div>
        )
      })}
    </div>
  )
}
